import equal from 'fast-deep-equal';
import { useEffect, useState, type RefObject } from 'react';
import type { FieldValues, Path, UseFormProps } from 'react-hook-form';
import { useCustomForm } from '~/hooks/useCustomForm';
import type { PersonalFormValues, ProfessionalInfoValues, SensitiveInfoValues } from './types';

interface ProfileDraft<T> {
  source: T;
  values: T;
}

type DraftRef<T> = RefObject<ProfileDraft<T> | undefined>;
export interface ProfileDrafts {
  personal: DraftRef<PersonalFormValues>;
  sensitive: DraftRef<SensitiveInfoValues>;
  professional: DraftRef<ProfessionalInfoValues>;
}

/** Keep inactive section drafts only for this Profile page's lifetime.
 * There is still one mounted form. A changed saved section supersedes its draft;
 * an unrelated section's save may rehydrate identical values with new references.
 */
export const useProfileDraftForm = <T extends FieldValues>(
  options: UseFormProps<T> & { values: T },
  draft?: DraftRef<T>
) => {
  const [initialDraft] = useState(() => {
    const stored = draft?.current;
    if (!stored || !equal(stored.source, options.values)) return undefined;
    // Revalidate only edited fields so an incomplete saved profile stays quiet.
    const changedFields = Object.keys(stored.values).filter(
      (name) => !equal(stored.values[name], stored.source[name])
    ) as Path<T>[];
    return { values: stored.values, changedFields };
  });
  const methods = useCustomForm<T>(options);
  const { getValues, reset, trigger } = methods;
  const source = options.values;

  useEffect(() => {
    if (!initialDraft) return;
    reset(initialDraft.values, { keepDefaultValues: true });
    if (initialDraft.changedFields.length) void trigger(initialDraft.changedFields);
  }, [initialDraft, reset, trigger]);

  useEffect(
    () => () => {
      if (draft) draft.current = { source, values: getValues() };
    },
    [draft, getValues, source]
  );

  return methods;
};
